import { calculateDeadlineDate } from '@/utils/publication-classifier'
import type { DeadlineUnit, Efeito, MemoryLine, Natureza } from '@/services/deadline-service'

export const NATUREZA_LABELS: Record<Natureza, string> = {
  FERIADO: 'Feriado',
  PONTO_FACULTATIVO: 'Ponto facultativo',
  SUSPENSAO: 'Suspensão',
  RECESSO: 'Recesso',
}

export const EFEITO_LABELS: Record<Efeito, string> = {
  SEM_EXPEDIENTE: 'Sem expediente',
  PRAZOS_SUSPENSOS: 'Prazos suspensos',
  EXPEDIENTE_REDUZIDO: 'Expediente reduzido',
}

export const EVENTO_LABELS: Record<MemoryLine['evento'], string> = {
  DISPONIBILIZACAO: 'Disponibilização',
  PUBLICACAO: 'Publicação',
  INICIO: 'Início (1º dia)',
  CONTADO: 'Contado',
  PULADO: 'Pulado',
  PRORROGADO: 'Prorrogado',
  VENCIMENTO: 'Vencimento',
  PRAZO_INTERNO: 'Prazo interno',
}

export const UNIT_LABELS: Record<DeadlineUnit, string> = { UTEIS: 'dias úteis', CORRIDOS: 'dias corridos' }

export interface LegacyComparison {
  /** Resultado do utilitário antigo (só soma dias úteis, sem feriados, a partir da disponibilização). */
  legacy: string
  differs: boolean
}

/**
 * Período de convivência: o `calculateDeadlineDate` do frontend deixou de ser a
 * fonte do prazo; aparece só para comparação quando diverge do cálculo da API.
 * Só faz sentido para prazo em dias úteis (o antigo não conhecia corridos).
 */
export function compareWithLegacy(input: { dataDisponibilizacao: string; dias: number; unidade: DeadlineUnit; prazoFatal: string }): LegacyComparison | null {
  if (input.unidade !== 'UTEIS' || !input.dataDisponibilizacao) return null
  const legacy = calculateDeadlineDate(input.dataDisponibilizacao, input.dias)
  return { legacy, differs: legacy !== input.prazoFatal.slice(0, 10) }
}

/** UFs atendidas por tribunal (espelho do catálogo da API, deadlines/domain/tribunals.ts). */
export const TRIBUNAL_UFS: Record<string, string[]> = {
  TJBA: ['BA'], TJSE: ['SE'], TJAL: ['AL'], TJPR: ['PR'], TJSP: ['SP'], TJPB: ['PB'],
  TRF1: ['BA'], TRF3: ['SP'], TRF4: ['PR'], TRF5: ['SE', 'AL', 'PB'],
}

export const UFS_BRASIL: readonly string[] = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA',
  'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
]

export interface UfChoice {
  /** UF fixa (TJ: a dele) ou null (TRF: a pessoa escolhe; vazio = Seção não identificada). */
  fixed: string | null
  options: readonly string[]
}

/**
 * UF do cálculo no formulário. TJ: a do tribunal, sem escolha. TRF: qualquer UF
 * do país, vazio por padrão — a Seção não se deduz do catálogo (a API conta sem
 * feriado estadual e avisa). null = tribunal sem calendário.
 */
export function ufChoice(tribunal: string | null | undefined): UfChoice | null {
  const sigla = String(tribunal ?? '').toUpperCase()
  const ufs = TRIBUNAL_UFS[sigla]
  if (!ufs) return null
  if (sigla.startsWith('TRF')) return { fixed: null, options: UFS_BRASIL }
  return { fixed: ufs.length === 1 ? ufs[0] : null, options: ufs }
}
